import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import {
  RoleApiDownload,
  RoleDto,
  RoleFilterSelects,
  RoleFormSelects,
  RoleGridDto,
  RoleListQuery,
  RoleListResult,
  RoleRequest,
  RoleSelectDto,
  RoleToneDto,
} from './roles-api.types';
import { mapSettingsError, SettingsApiError } from './settings-api.errors';
import { SETTINGS_API_URL } from './settings-api.tokens';

const REQUEST_TIMEOUT_MS = 15_000;
const LIST_PAGE_SIZE = 100;
const ROLES_PATH = '/roles';

@Injectable({ providedIn: 'root' })
export class RolesApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(SETTINGS_API_URL).replace(/\/+$/, '')}${ROLES_PATH}`;

  list(query: RoleListQuery = {}): Observable<RoleListResult> {
    return this.http.get<unknown>(this.baseUrl, { params: roleListParams(query) }).pipe(
      timeout(REQUEST_TIMEOUT_MS),
      retry({ count: 1, delay: error => retryDelay(error) }),
      map(unwrapList),
      catchError(error => throwError(() => mapRoleError(error))),
    );
  }

  formSelects(): Promise<RoleFormSelects> {
    return this.safeGet(`${this.baseUrl}/form-selects`, unwrapFormSelects);
  }

  filterSelects(): Promise<RoleFilterSelects> {
    return this.safeGet(`${this.baseUrl}/filter-selects`, unwrapFilterSelects);
  }

  getById(uuid: string): Promise<RoleDto> {
    return this.safeGet(`${this.baseUrl}/${uuid}`, unwrapRole);
  }

  create(payload: RoleRequest): Promise<RoleDto> {
    return this.mutate(this.http.post<unknown>(this.baseUrl, payload), unwrapRole);
  }

  update(uuid: string, payload: RoleRequest): Promise<RoleDto> {
    return this.mutate(
      this.http.put<unknown>(`${this.baseUrl}/${uuid}`, payload),
      unwrapRole,
    );
  }

  activate(uuid: string): Promise<void> {
    return this.mutate(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/activate`, null),
      () => undefined,
    );
  }

  inactivate(uuid: string): Promise<void> {
    return this.mutate(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/inactivate`, null),
      () => undefined,
    );
  }

  remove(uuid: string): Promise<void> {
    return this.mutate(this.http.delete<unknown>(`${this.baseUrl}/${uuid}`), () => undefined);
  }

  exportExcel(query: RoleListQuery = {}): Promise<RoleApiDownload> {
    return this.download(
      `${this.baseUrl}/excel`,
      roleListParams(query, false),
      'roles.xlsx',
    );
  }

  private safeGet<T>(url: string, project: (body: unknown) => T): Promise<T> {
    return this.request(
      this.http
        .get<unknown>(url)
        .pipe(retry({ count: 1, delay: error => retryDelay(error) })),
      project,
    );
  }

  private mutate<T>(source: Observable<unknown>, project: (body: unknown) => T): Promise<T> {
    return this.request(source, project);
  }

  private async request<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(REQUEST_TIMEOUT_MS)));
      return project(body);
    } catch (error) {
      throw mapRoleError(error);
    }
  }

  private async download(
    url: string,
    params: HttpParams,
    fallbackFilename: string,
  ): Promise<RoleApiDownload> {
    try {
      const response = await firstValueFrom(
        this.http
          .get(url, { params, observe: 'response', responseType: 'blob' })
          .pipe(
            timeout(REQUEST_TIMEOUT_MS),
            retry({ count: 1, delay: error => retryDelay(error) }),
          ),
      );
      return toDownload(response, fallbackFilename);
    } catch (error) {
      throw mapRoleError(error);
    }
  }
}

function retryDelay(error: unknown): Observable<number> {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      const seconds = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
      return timer(seconds * 1000);
    }
    if (error.status >= 500 || error.status === 0) return timer(300);
  }
  return throwError(() => error);
}

function roleListParams(query: RoleListQuery, includePagination = true): HttpParams {
  let params = new HttpParams();
  if (includePagination) {
    params = params
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? LIST_PAGE_SIZE));
  }
  if (query.name) params = params.set('filters[name]', query.name);
  if (query.description) params = params.set('filters[description]', query.description);
  if (query.realEstate) params = params.set('filters[realEstate]', query.realEstate);
  if (query.supplier) params = params.set('filters[supplier]', query.supplier);
  params = setCsv(params, 'select[type]', query.typeUuids);
  params = setCsv(params, 'select[classification]', query.classificationUuids);
  params = setCsv(params, 'select[status]', query.statusUuids);
  if (query.sortBy) params = params.set('sortBy', query.sortBy);
  if (query.order) params = params.set('order', query.order);
  return params;
}

function setCsv(
  params: HttpParams,
  key: string,
  values: readonly string[] | undefined,
): HttpParams {
  return values?.length ? params.set(key, values.join(',')) : params;
}

function mapRoleError(error: unknown): SettingsApiError {
  const mapped = mapSettingsError(error);
  if (error instanceof HttpErrorResponse && error.error && typeof error.error === 'object') {
    const business = (error.error as { error?: unknown }).error;
    if (typeof business === 'string' && business.trim()) {
      mapped.message = business.trim();
    }
  }
  return mapped;
}

function toDownload(
  response: HttpResponse<Blob>,
  fallbackFilename: string,
): RoleApiDownload {
  if (!response.body) throw new SettingsApiError('unexpected', 0);
  return {
    blob: response.body,
    filename:
      contentDispositionFilename(response.headers.get('Content-Disposition')) ??
      fallbackFilename,
  };
}

function contentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.replace(/^"|"$/g, ''));
    } catch {
      return encoded.replace(/^"|"$/g, '');
    }
  }
  return header.match(/filename="?([^";]+)"?/i)?.[1] ?? null;
}

function unwrapData(body: unknown): unknown {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

function unwrapList(body: unknown): RoleListResult {
  const page = asRecord(unwrapData(body));
  const nested = page?.['data'];
  if (!Array.isArray(nested)) throw new SettingsApiError('unexpected', 0);
  const items = nested.map(item => {
    const row = normalizeGrid(item);
    if (!row) throw new SettingsApiError('unexpected', 0);
    return row;
  });
  const pagination = asRecord(page?.['pagination']);
  const totalRecords = pagination?.['totalRecords'];
  return {
    items,
    total: typeof totalRecords === 'number' ? totalRecords : items.length,
  };
}

function unwrapRole(body: unknown): RoleDto {
  const role = normalizeDetail(unwrapData(body));
  if (!role) throw new SettingsApiError('unexpected', 0);
  return role;
}

function unwrapFormSelects(body: unknown): RoleFormSelects {
  const data = asRecord(unwrapData(body));
  if (!data) throw new SettingsApiError('unexpected', 0);
  return {
    types: selectList(data['types']),
    classifications: selectList(data['classifications']),
    realEstates: selectList(data['realEstates']),
    suppliers: selectList(data['suppliers']),
  };
}

function unwrapFilterSelects(body: unknown): RoleFilterSelects {
  const data = asRecord(unwrapData(body));
  if (!data) throw new SettingsApiError('unexpected', 0);
  return {
    types: selectList(data['types']),
    classifications: selectList(data['classifications']),
    statuses: selectList(data['statuses']),
  };
}

function selectList(value: unknown): RoleSelectDto[] {
  if (!Array.isArray(value)) throw new SettingsApiError('unexpected', 0);
  return value.map(item => {
    const option = selectDto(item);
    if (!option) throw new SettingsApiError('unexpected', 0);
    return option;
  });
}

function normalizeGrid(value: unknown): RoleGridDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = stringField(record, 'uuid');
  const name = stringField(record, 'name');
  const type = selectDto(record['type']);
  const classification = selectDto(record['classification']);
  const status = toneDto(record['status']);
  const realEstate = nullableSelect(record['realEstate']);
  const supplier = nullableSelect(record['supplier']);
  const description = nullableString(record['description']);
  if (!uuid || !name || !type || !classification || !status) return null;
  if (realEstate === undefined || supplier === undefined || description === undefined) {
    return null;
  }
  return {
    uuid,
    name,
    type,
    classification,
    realEstate,
    supplier,
    description,
    status,
    protectedRole: record['protectedRole'] === true,
  };
}

function normalizeDetail(value: unknown): RoleDto | null {
  const grid = normalizeGrid(value);
  const record = asRecord(value);
  if (!grid || !record) return null;
  const code = stringField(record, 'code');
  const createdAt = stringField(record, 'createdAt');
  const updatedAt = stringField(record, 'updatedAt');
  if (!code || !createdAt || !updatedAt || typeof record['assigned'] !== 'boolean') {
    return null;
  }
  return {
    ...grid,
    code,
    assigned: record['assigned'],
    protectedRole: record['protectedRole'] === true,
    createdAt,
    updatedAt,
  };
}

function nullableSelect(value: unknown): RoleSelectDto | null | undefined {
  if (value == null) return null;
  return selectDto(value) ?? undefined;
}

function nullableString(value: unknown): string | null | undefined {
  if (value == null) return null;
  return typeof value === 'string' ? value : undefined;
}

function selectDto(value: unknown): RoleSelectDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = stringField(record, 'uuid');
  const name = stringField(record, 'name');
  return uuid && name ? { uuid, name } : null;
}

function toneDto(value: unknown): RoleToneDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const name = stringField(record, 'name');
  const toneValue = stringField(record, 'value');
  const tone = stringField(record, 'tone');
  return name && toneValue && tone ? { name, value: toneValue, tone } : null;
}

function stringField(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}
